import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  combinedSearchThunk,
  loadingCombinedSearch,
  selectDrugResults,
  selectTopicQuery,
  selectTopicResults,
} from "../../../features/enhancedSearch";
import style from "./EnhancedSearchResults.module.scss";
import classNames from "classnames";
import PropTypes from "prop-types";
import { mapDrugs, summarizeDrugs } from "./mapper/mapDrugs";
import Loader from "../../Loader/Loader";
import AppSearchFilters from "../AppSearchFilters/AppSearchFilters";
import AnimatedPopup from "../../AnimatedComponents/AnimatedPopup";
import SearchResultsList from "./SearchResultList";
import useIsWebScreen from "../../../hooks/useScreenSize";
import { extractAllBoldText } from "../../../utils/extractAllBoldText";

const EnhancedSearchResults = ({
  search,
  selectedTab,
  setSelectedTab,
  isAppMode,
  isWebModernisationEnabled,
  navigate,
}) => {
  const dispatch = useDispatch();
  const previousSearchQuery = useSelector(selectTopicQuery);
  const [showFilters, setShowFilters] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [skipLoading, setSkipLoading] = useState(false);
  const [filterNumber, setFilterNumber] = useState(0);
  const { isDesktop } = useIsWebScreen();

  useEffect(() => {
    const searchParams = new URLSearchParams(search);
    if (searchParams.has("appendedinputbuttons")) {
      setSkipLoading(false);
      const urlSearch = searchParams.get("appendedinputbuttons");
      const guidelineFilter = searchParams.getAll("guidelinefilter");
      const searchWithin = searchParams.get("searchWithin");
      const filters = searchParams.getAll("guidelinefilter");
      if (filters.length) {
        setFilterNumber(filters.length);
      }
      setSearchTerm(urlSearch);
      dispatch(
        combinedSearchThunk({
          searchText: urlSearch,
          guidelineFacet: guidelineFilter?.length ? guidelineFilter : undefined,
          top: previousSearchQuery?.top,
          searchWithin: searchWithin || "",
        }),
      );
    }
  }, [search]);

  const drugResults = useSelector(selectDrugResults);
  const searchParams = new URLSearchParams(search);

  const isLoading = useSelector(loadingCombinedSearch);
  const remappedDrugs = mapDrugs(drugResults);
  const drugSummary = summarizeDrugs(drugResults);

  const remappedDrugResults = { results: remappedDrugs };
  const searchResults = [useSelector(selectTopicResults), remappedDrugResults];

  const noResultsMessage = selectedTab === 0 ? "topics" : "drug indications";
  const resultCount =
    selectedTab === 0
      ? searchResults[selectedTab]?.totalCount
      : searchResults[selectedTab]?.results?.length;
  const correctedTerm =
    searchParams.get("correction") ||
    extractAllBoldText(searchResults[0]?.results?.[0]?.title) ||
    extractAllBoldText(searchResults[0]?.results?.[0]?.summary) ||
    "";

  const loadMore = () => {
    const searchParams = new URLSearchParams(search);
    const guidelineFilter = searchParams.get("guidelinefilter");
    const searchWithin = searchParams.get("searchWithin");
    setSkipLoading(true);
    dispatch(
      combinedSearchThunk({
        searchText: previousSearchQuery.searchText,
        guidelineFacet: guidelineFilter,
        top: previousSearchQuery?.top ? previousSearchQuery.top : 10,
        skip: previousSearchQuery?.skip ? previousSearchQuery?.skip + 10 : 10,
        previousResults: searchResults[0].results,
        searchWithin: searchWithin,
      }),
    );
  };

  return (
    <div
      className={classNames(style.searchPage, "col-md-8 columnBlockLayout tabContainer regular")}
    >
      <div
        className={classNames(
          {
            "size-20": isAppMode || !isDesktop !== true,
            "size-16": isAppMode || isDesktop === true,
          },
          style.tabSection,
        )}
      >
        <button
          className={classNames("size-16", {
            [style.active]: selectedTab === 0,
          })}
          onClick={() => setSelectedTab(0)}
        >
          Topic
        </button>

        <button
          className={classNames("size-16", {
            [style.active]: selectedTab === 1,
          })}
          onClick={() => setSelectedTab(1)}
        >
          Drug
        </button>
        <div
          className={classNames(
            {
              "size-16": isAppMode || !isDesktop !== true,
              "size-12 regular": isAppMode || isDesktop === true,
            },
            style.resultCount,
          )}
        >
          {/* {!isLoading && selectedTab === 0 && (
            <span className={}>{resultCount ?? 0} results</span>
          )} */}
          {(isAppMode || !isDesktop) && (
            <div className={style.iconWrapper}>
              <button type="button" onClick={() => setShowFilters(!showFilters)}>
                {filterNumber > 0 && <span className={style.counter}>{filterNumber}</span>}
                <i className="tg-icons-Filters size-14"></i>
              </button>
            </div>
          )}
        </div>
      </div>
      {correctedTerm && resultCount !== undefined && resultCount > 0 && (
        <div
          id="did-you-mean"
          searchTerm={correctedTerm}
          className={classNames("size-16", style.didYouMean)}
        >
          Did you mean <em>"{correctedTerm}"</em>? Showing results for this term.
        </div>
      )}
      <ul className={style.resultList}>
        {isLoading && !skipLoading ? (
          <div className={style.loader}>
            <Loader constrainedHeight={true} />
          </div>
        ) : (
          <SearchResultsList
            searchResults={searchResults}
            drugSummary={drugSummary}
            selectedTab={selectedTab}
            searchTerm={searchTerm}
            isAppMode={isAppMode}
            isWebModernisationEnabled={isWebModernisationEnabled}
            noResultsMessage={noResultsMessage}
            resultCount={resultCount}
          />
        )}
      </ul>
      {(isAppMode || !isDesktop) &&
        !isLoading &&
        resultCount > searchResults[selectedTab]?.results?.length && (
          <button
            className={classNames("size-14 medium", style.loadMore)}
            onClick={loadMore}
            data-testid="load-more"
          >
            <i className={classNames("tg-icons-ChevronDown", style.loadMoreIcon)}></i>
            More search results
          </button>
        )}
      {(isAppMode || !isDesktop) && (
        <AnimatedPopup show={showFilters}>
          <AppSearchFilters setShowFilters={setShowFilters} search={search} navigate={navigate} />
        </AnimatedPopup>
      )}
    </div>
  );
};

EnhancedSearchResults.propTypes = {
  search: PropTypes.string,
  isAppMode: PropTypes.bool,
  selectedTab: PropTypes.number,
  setSelectedTab: PropTypes.func,
};

export default EnhancedSearchResults;
